// Bounded-retry counter for queued offline encounter diffs, the direct sibling
// of recordingPoisonRegistry (both sit on the shared createBoundedTtlStore).
//
// The native shell never abandons a queued diff: it re-offers every unacked one
// on every sync, and the only way a diff leaves the queue is a `syncedEncounter`
// ack from the WebView. Since the replay now acks ONLY a write the server
// actually accepted (2xx), a PERMANENTLY unreplayable diff -- a deleted
// encounter, a malformed payload -- is re-offered forever, and each pass costs
// one doomed request and one error toast the user can do nothing about.
//
// So count the failures per diff and give up at the bound, exactly the way the
// recording queue gives up on a confirmed-orphaned recording: drop it ONCE with
// an honest, distinct notice rather than re-toasting the same failure until the
// end of time. Unlike the recording case the payload is NOT kept -- acking
// deletes the diff on the shell -- which is why the give-up notice has to say
// plainly that the changes were discarded, and why the caller strikes ONLY on a
// genuine server rejection (usePostMessages): an outage is not a verdict.
//
// Storage is per-device and self-healing (worst case after eviction the diff
// gets another full round of attempts, which is strictly the safe direction).
// Keys are queue identity plus a hash of the queued body -- never the body
// itself, which is PHI.
import { createBoundedTtlStore } from "./boundedTtlStore";

// THE WINDOW, and why it is this one.
//
// It used to be "5 failing sync passes inside 7 days of the first failure",
// which satisfied neither requirement:
//
//   * Eventual give-up. A device that syncs fewer than ~5 times a week — the
//     intermittent-connectivity device this whole feature targets — had its
//     counter expire before it could ever reach 5. Give-up never fired, and the
//     unbounded nag the registry was built to end came straight back.
//   * Immunity to incidents. Five reconnect flaps inside ONE server incident
//     (a GraalVM module-load failure answers 500 to every write, fleet-wide,
//     for as long as it lasts) burned the whole budget in minutes and destroyed
//     real clinical edits.
//
// So strikes are counted in DISTINCT UTC DAYS, three of them, in a window fixed
// at 30 days from the first strike:
//
//   * a permanently rejected diff needs 3 distinct sync-days inside 30 —
//     reachable by a device that connects roughly weekly. A device that
//     connects fewer than 3 times a month never gives up; it also sees at most
//     one toast per connection, which is not nagging. That is the honest
//     accepted edge.
//   * a same-day incident, however many reconnect flaps or retry wakes it
//     produces, costs exactly ONE strike. An incident must persist across three
//     separate calendar days to destroy a diff.
//   * the window is FIXED from the first strike, never slid. Restamping would
//     let a once-a-day failure accumulate forever; fixed-from-first also keeps
//     January's failures from condemning a February retry, since a January
//     entry expires 30 days after its own first failure.
const store = createBoundedTtlStore({
  key: "offlineDiffReplayFailures.v1",
  ttlMs: 30 * 24 * 60 * 60 * 1000,
  maxEntries: 300,
});

export const MAX_DIFF_REPLAY_STRIKE_DAYS = 3;

// UTC day index. A timezone-correct local day buys nothing here and adds DST
// edges.
const DAY_MS = 24 * 60 * 60 * 1000;
const dayIndex = (now = Date.now()) => Math.floor(now / DAY_MS);

// djb2. Only needs to be stable and collision-resistant enough to tell two
// queued bodies apart on one device; it is never sent anywhere.
const hashString = (value) => {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(36);
};

// Identity for one queued diff: the encounter, the shell's queue row id (or
// timestamp), AND a hash of the queued body.
//
// The content hash is not optional garnish. The shell upserts a chat's pending
// diff in place, so the queue row id is INVARIANT across content revisions --
// keying on it alone let strikes accumulate across completely different edits,
// and a brand-new edit could inherit four strikes and be destroyed on its first
// failure. Hashing the body means new content is a new counter, which is what
// the bound is supposed to mean.
//
// Returns null for an UNKEYABLE diff — and unkeyable means "never strikeable",
// because recordDiffReplayFailure(null) is 0 and 0 can never reach the bound.
// All three reasons live here, in one place, rather than as rules scattered
// across the caller:
//
//   temp- ....... a create is not an edit, it is the ENTIRE encounter. Dropping
//                 it deletes a visit that exists nowhere but this device and
//                 permanently orphans every native recording captured against
//                 it (the durable chat is found by `local:offlineId`, which only
//                 the successful create ever stamps). No number of failures
//                 makes destroying a clinical encounter the right answer, so a
//                 create retries indefinitely. The cost is a repeated toast, not
//                 lost care.
//   non-string
//   diff ........ String({}) is "[object Object]", so every such diff hashed to
//                 ONE shared key and their strikes pooled.
//   no queue id . same pooling through the old "-" placeholder.
export function buildDiffReplayKey(chat) {
  if (!chat || chat.chatId == null) return null;
  if (typeof chat.chatId === "string" && chat.chatId.startsWith("temp-")) return null;
  if (typeof chat.diff !== "string") return null;
  const queueId = chat.id != null ? chat.id : chat.timestamp;
  if (queueId == null) return null;
  return `${String(chat.chatId)}|${String(queueId)}:${hashString(chat.diff)}`;
}

// Count one strike. Returns the running count of DISTINCT STRIKE-DAYS (0 for an
// unkeyable diff, which therefore can never reach the bound).
//
// A second strike on a day already counted is a no-op — it returns the existing
// count and writes NOTHING, which is both the incident immunity described above
// and, for free, the removal of a localStorage read-modify-write in the common
// repeat case.
export function recordDiffReplayFailure(key, now = Date.now()) {
  if (!key) return 0;
  const k = String(key);
  const today = dayIndex(now);
  const list = store.load();
  const existing = list.find((entry) => entry.k === k);
  if (existing && existing.d === today) return existing.n || 0;
  const strikeDays = (existing?.n || 0) + 1;
  // Keep the ORIGINAL timestamp: the record must expire a fixed span after the
  // FIRST strike, not after the last one.
  const ts = existing?.ts || now;
  const next = list.filter((entry) => entry.k !== k);
  next.push({ k, n: strikeDays, ts, d: today });
  store.save(next);
  return strikeDays;
}

// A replay landed (or the diff was dropped): the counter has no more work to do.
export function clearDiffReplayFailure(key) {
  if (!key) return;
  const k = String(key);
  const list = store.load();
  const next = list.filter((entry) => entry.k !== k);
  // Single pass: only write when something actually went away.
  if (next.length === list.length) return;
  store.save(next);
}

// Test helper.
export function clearDiffReplayRegistry() {
  store.clear();
}
